import { Observable, map } from "rxjs";
import HttpService from "./HttpService";
import { url } from "./APIEndPoints";
import { buildParamString, IAPIResponse } from "@mindmemobile/mm-shared-global";
import {
  IAPICreateDonationResponseModel,
  IAPICreateUpdateDonationModel,
  IAPIDonarsGridModel,
  IAPIDonationGridModel,
  IAPIGetEditDonationModel,
} from "../interfaces/IAPIDonationModels";
import { IAPIProductReferenceInPurchaseResponse } from "../interfaces";

class DonationService {
  private httpService: HttpService;

  constructor() {
    this.httpService = new HttpService(
      undefined,
      `${process.env.REACT_APP_API_KEY}`,
      process.env.REACT_APP_API_BASE_URL
    );
  }

  save(
    data: IAPICreateUpdateDonationModel
  ): Observable<IAPICreateDonationResponseModel> {
    return this.httpService
      .post<
        IAPIResponse<IAPICreateDonationResponseModel>
      >(url.createDonation, data)
      .pipe(
        map((result) => {
          return result.result;
        })
      );
  }

  update(data: IAPICreateUpdateDonationModel): Observable<boolean> {
    return this.httpService
      .put<IAPIResponse<boolean>>(url.updateDonation, data)
      .pipe(map((result) => result.result));
  }

  getDonations(
    accountId: string,
    pageNumber: number,
    pageSize: number,
    searchValue: string
  ): Observable<IAPIResponse<IAPIDonationGridModel[]>> {
    const params: string = buildParamString({
      accountId: accountId,
      pageNumber: pageNumber,
      pageSize: pageSize,
      searchValue: searchValue,
    });

    return this.httpService.get<IAPIResponse<IAPIDonationGridModel[]>>(
      url.getDonations,
      params
    );
  }

  getDonation(
    accountId: string,
    id: string
  ): Observable<IAPIResponse<IAPIGetEditDonationModel>> {
    const params: string = buildParamString({
      accountId: accountId,
      id: id,
    });

    return this.httpService.get<IAPIResponse<IAPIGetEditDonationModel>>(
      url.getDonation,
      params
    );
  }

  getDonars(
    accountId: string,
    pageNumber: number,
    pageSize: number,
    searchValue: string
  ): Observable<IAPIResponse<IAPIDonarsGridModel[]>> {
    const params: string = buildParamString({
      accountId: accountId,
      pageNumber: pageNumber,
      pageSize: pageSize,
      searchValue: searchValue,
    });

    return this.httpService.get<IAPIResponse<IAPIDonarsGridModel[]>>(
      url.getDonars,
      params
    );
  }

  /* Whole-grid downloads built by the API; the caller only saves the Blob. searchValue applies the
     same filter the grid uses. */
  exportDonations(
    accountId: string,
    format: "csv" | "xlsx",
    searchValue: string
  ): Observable<Blob> {
    const params: string = buildParamString({
      accountId: accountId,
      format: format,
      searchValue: searchValue,
    });
    return this.httpService.getBlob(url.exportDonations, params);
  }

  exportDonors(
    accountId: string,
    format: "csv" | "xlsx",
    searchValue: string
  ): Observable<Blob> {
    const params: string = buildParamString({
      accountId: accountId,
      format: format,
      searchValue: searchValue,
    });
    return this.httpService.getBlob(url.exportDonors, params);
  }

  deleteItem(
    accountId: string,
    itemId: string
  ): Observable<IAPIResponse<boolean>> {
    const body = {
      accountId: accountId,
      itemId: itemId,
    };
    return this.httpService.delete<IAPIResponse<boolean>>(
      url.deleteDonation,
      body
    );
  }

  updateStatus(
    accountId: string,
    id: string,
    isActive: boolean
  ): Observable<IAPIResponse<boolean>> {
    const request = {
      accountId: accountId,
      id: id,
      isActive: isActive,
    };

    return this.httpService.put<IAPIResponse<boolean>>(
      url.donationStatusUpdate,
      request
    );
  }

  geDonationReferenceInPurchases(
    accountId: string,
    id: string
  ): Observable<IAPIResponse<IAPIProductReferenceInPurchaseResponse>> {
    const params: string = buildParamString({
      accountId: accountId,
      id: id,
    });

    return this.httpService.get<
      IAPIResponse<IAPIProductReferenceInPurchaseResponse>
    >(url.getDonationReferenceInPurchase, params);
  }
}

export default new DonationService();
